// frontend/src/components/BookReading/ChapterList.js
//#region Imports
import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { ListGroup, Form, Button, InputGroup, Spinner, Alert, ProgressBar } from "react-bootstrap";
import { FaSearch, FaSeedling, FaPlay, FaCheck, FaLock } from "react-icons/fa";
import axios from "axios";
import { API_ENDPOINTS } from "../../configs/apiConfig";
import { BookReadingContext } from './BookReading';
import { AuthContext } from "../../contexts/AuthContext";
import HiddenItemNotificationModal from "./HiddenItemNotificationModal";
import PaginationComponent from "../PaginationComponent";
import useMediaQuery from "../../utils/useMediaQuery";
import { useAlertContext } from '../../contexts/AlertContext';
import { getReadChapterIds, getLastRead, bookProgress, coverGradient } from './readingProgress';
import '../../styles/App.css';
//#endregion

//#region Componente Principal
function ChapterList() {
  //#region Declarações
  const { bookId } = useParams();
  const navigate = useNavigate();
  const {
    books,
    selectedBook,
    setSelectedBook,
    setSelectedChapter,
    translations,
    resetToBooks
  } = useContext(BookReadingContext);
  const { username } = useContext(AuthContext);

  const { showError } = useAlertContext();

  const [searchKeyword, setSearchKeyword] = useState("");
  const [oldSearchKeyword, setOldSearchKeyword] = useState("");
  const [filteredChapters, setFilteredChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState("");
  const [showHighlight, setShowHighlight] = useState(false);
  const [showHiddenNotification, setShowHiddenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Calcular dados da paginação
  const sortedChapters = filteredChapters.slice().sort((a, b) => a.position - b.position);
  const totalItems = sortedChapters.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentChapters = sortedChapters.slice(startIndex, endIndex);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Progresso de leitura
  const readSet = getReadChapterIds(username);
  const lastRead = getLastRead(username);
  const prog = selectedBook ? bookProgress(selectedBook, readSet) : { read: 0, total: 0, pct: 0 };

  // Capítulo de "Continuar": último lido neste livro ou o primeiro visível
  const visibleSorted = selectedBook
    ? (selectedBook.chapters || []).filter((c) => c.visible !== false).sort((a, b) => a.position - b.position)
    : [];
  const continueChapterId =
    (lastRead && Number(lastRead.bookId) === Number(selectedBook?.id) && lastRead.chapterId)
      ? lastRead.chapterId
      : (visibleSorted[0]?.id);
  //#endregion

  //#region Funções Auxiliares
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return <mark key={index} className="highlight">{part}</mark>;
      }
      return part;
    });
  };

  const handleHiddenItemClick = (item, type) => {
    const message = type === 'chapter'
      ? translations.chapterInEditingMessage.replace('{title}', item.title)
      : translations.bookInEditingMessage.replace('{title}', item.title);

    setNotificationMessage(message);
    setShowHiddenNotification(true);
  };

  const backToBooks = () => {
    resetToBooks();
  };
  //#endregion

  //#region Manipulação de Busca
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      setFilteredChapters(selectedBook.chapters || []);
      setError("");
      setShowHighlight(false);
      return;
    }

    if (searchKeyword === oldSearchKeyword) {
      setFilteredChapters(selectedBook.chapters || []);
      setError("");
      setShowHighlight(false);
      setSearchKeyword("");
      setOldSearchKeyword("");
      return;
    }

    setOldSearchKeyword(searchKeyword);
    setSearchLoading(true);
    setError("");
    setShowHighlight(true);

    try {
      const auth = localStorage.getItem("auth");
      const endpoint = API_ENDPOINTS.CHAPTER_FILTER(searchKeyword, selectedBook.id);
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Basic ${auth}` },
      });
      setFilteredChapters(response.data || []);
    } catch (err) {
      setError(translations.failChapterSearch + (err.message || "Erro desconhecido"));
      setFilteredChapters(selectedBook.chapters || []);
      setShowHighlight(false);
    } finally {
      setSearchLoading(false);
    }
  };
  //#endregion

  //#region Manipulação de Capítulos
  const handleChapterSelect = async (chapter) => {
    try {
      setSelectedChapter(chapter);
      navigate(`../chapter/${chapter.id}`);
    } catch (error) {
      showError(translations.failChapterAccess + error.message);
    }
  };

  const handleContinue = () => {
    if (continueChapterId) {
      navigate(`../chapter/${continueChapterId}`);
    }
  };
  //#endregion

  //#region Efeitos (useEffect) Carregar dados do livro quando o componente monta
  useEffect(() => {
    const loadBookData = async () => {
      setLoading(true);

      try {
        let book = books.find(b => b.id.toString() === bookId);

        if (!book) {
          const auth = localStorage.getItem('auth');
          const response = await axios.get(API_ENDPOINTS.BOOKS_BY_ID(bookId), {
            headers: { Authorization: `Basic ${auth}` },
          });
          book = response.data;
        }

        if (book) {
          setSelectedBook(book);
          setFilteredChapters(book.chapters || []);
        } else {
          showError(translations.bookNotFound);
          navigate('..');
        }
      } catch (error) {
        console.error('Erro ao carregar livro:', error);
        showError(translations.failBooks + error.message);
        navigate('..');
      } finally {
        setLoading(false);
      }
    };

    if (bookId && books.length > 0) {
      loadBookData();
    }
  }, [bookId, books, setSelectedBook, showError, navigate, translations]);

  // Reset filtros quando o livro muda
  useEffect(() => {
    if (selectedBook) {
      setFilteredChapters(selectedBook.chapters || []);
      setSearchKeyword("");
      setOldSearchKeyword("");
      setError("");
      setShowHighlight(false);
      setCurrentPage(1);
    }
  }, [selectedBook]);

  // Reset página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredChapters]);
  //#endregion

  //#region Renderização
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{translations.loading}</span>
        </Spinner>
        <p className="mt-3">{translations.loadingBook}</p>
      </div>
    );
  }

  if (!selectedBook) {
    return (
      <Alert variant="warning">
        <Alert.Heading>{translations.bookNotFound}</Alert.Heading>
        <p>{translations.bookNotFoundDescription}</p>
        <Button variant="outline-warning" onClick={backToBooks}>
          {translations.backToBooks}
        </Button>
      </Alert>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Button variant="link" className="p-0" onClick={backToBooks}>
              {translations.books}
            </Button>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {selectedBook.title}
          </li>
        </ol>
      </nav>

      {/* Cabeçalho do livro: capa + título + progresso + continuar */}
      <div className="book-header-strip mb-4">
        <div className="book-header-cover" style={{ background: coverGradient(selectedBook) }}>
          <FaSeedling />
        </div>
        <div className="flex-grow-1">
          <h2 className="book-header-title">{selectedBook.title}</h2>
          <div className="book-header-meta">
            {prog.total} {translations.chapters}
            {prog.read > 0 && <> · {prog.pct}% {translations.readLabel}</>}
          </div>
          {prog.read > 0 && (
            <ProgressBar now={prog.pct} className="book-progress mt-2" style={{ maxWidth: 320 }} />
          )}
        </div>
        {continueChapterId && (
          <Button variant="brand" onClick={handleContinue} className="flex-shrink-0">
            <FaPlay className="me-2" />
            {prog.read > 0 ? translations.continueLabel : translations.startReading}
          </Button>
        )}
      </div>

      {/* Busca dentro do livro */}
      <Form className="mb-3" onSubmit={handleSubmit}>
        <InputGroup>
          <InputGroup.Text><FaSearch /></InputGroup.Text>
          <Form.Control
            type="text"
            placeholder={translations.enterKeyword}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            disabled={searchLoading}
          />
          <Button
            type="submit"
            variant={(searchKeyword && searchKeyword === oldSearchKeyword) ? "outline-secondary" : "brand"}
            onClick={handleSearch}
            disabled={searchLoading}
          >
            {searchLoading ? translations.loading : (searchKeyword && searchKeyword === oldSearchKeyword) ? translations.clear : translations.search}
          </Button>
        </InputGroup>
      </Form>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Sumário */}
      <h5 className="toc-heading">{translations.summary}</h5>
      <ListGroup variant="flush" className="toc-list mb-3">
        {currentChapters.map((chapter, idx) => {
          if (!chapter.visible) {
            return (
              <ListGroup.Item
                key={chapter.id}
                className="toc-item toc-item--locked"
                onClick={() => handleHiddenItemClick(chapter, "chapter")}
              >
                <span className="toc-num"><FaLock size={12} /></span>
                <span className="toc-title">
                  {chapter.title} <small className="text-muted">({translations.inEditing})</small>
                </span>
              </ListGroup.Item>
            );
          }

          const isRead = readSet.has(Number(chapter.id));
          const isCurrent = lastRead && Number(lastRead.chapterId) === Number(chapter.id);

          return (
            <ListGroup.Item
              key={chapter.id}
              action
              onClick={() => handleChapterSelect(chapter)}
              className={`toc-item${isCurrent ? ' toc-item--current' : ''}`}
            >
              <span className={`toc-num${isRead ? ' toc-num--read' : ''}`}>
                {startIndex + idx + 1}
              </span>
              <span className="toc-title">
                {highlightSearchTerm(chapter.title, showHighlight ? oldSearchKeyword : "")}
                {chapter.contentSnippet && showHighlight && (
                  <small className="d-block text-muted">
                    {highlightSearchTerm(chapter.contentSnippet, oldSearchKeyword)}
                  </small>
                )}
              </span>
              <span className="toc-status">
                {isRead ? (
                  <FaCheck className="toc-read" title={translations.readLabel} />
                ) : isCurrent ? (
                  <FaPlay className="toc-current" />
                ) : null}
              </span>
            </ListGroup.Item>
          );
        })}
      </ListGroup>

      {totalPages > 1 && (
        <PaginationComponent
          totalPages={totalPages}
          isMobile={isMobile}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          t={translations}
          tItemName={translations.chapters}
          indexOfFirst={startIndex}
          indexOfLast={endIndex}
          lengthTotal={totalItems}
        />
      )}

      <HiddenItemNotificationModal
        show={showHiddenNotification}
        onHide={() => setShowHiddenNotification(false)}
        message={notificationMessage}
        translations={translations}
      />
    </div>
  );
  //#endregion
}

export default ChapterList;
